import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useEffectiveRole } from '@/stores/authStore';
import { useAuth } from '@clerk/clerk-react';
import {
  AlertCircle,
  BookOpen,
  Download,
  FileText,
  FileVideo,
  Loader2,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api';

interface Manual {
  id: string;
  name: string;
  category: string;
  audience: string | null;
  file_type: string | null;
  download_count: number;
  created_at: string;
}

interface ManualsResponse {
  success: boolean;
  data: {
    assets: Manual[];
    categories: Record<string, number>;
    pagination: {
      has_more: boolean;
      next_cursor: string | null;
    };
    total_count: number;
    is_empty?: boolean;
    message?: string;
  };
}

// Category/topic options for manuals
const categoryOptions = [
  { value: 'all', label: 'All Topics' },
  { value: 'getting-started', label: 'Getting Started' },
  { value: 'training', label: 'Training' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'technical', label: 'Technical' },
  { value: 'business', label: 'Business' },
];

// File type options
const typeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'pdf', label: 'PDF Documents' },
  { value: 'video', label: 'Videos' },
];

// Accepted file types for upload
const ACCEPTED_TYPES = {
  pdf: ['application/pdf'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
};

const ALL_ACCEPTED = [...ACCEPTED_TYPES.pdf, ...ACCEPTED_TYPES.video].join(',');

export default function Manuals() {
  const { getToken } = useAuth();
  const effectiveRole = useEffectiveRole();
  const isAdmin = effectiveRole === 'hq_admin';
  const isOwner = effectiveRole === 'affiliate_owner';
  // Admins and Affiliate Owners can upload manuals (agents: download only)
  const canUpload = isAdmin || isOwner;

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [manualTitle, setManualTitle] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualTopic, setManualTopic] = useState('getting-started');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Manuals list state
  const [manuals, setManuals] = useState<Manual[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // Fetch manuals from API (using assets API with manuals folder)
  const fetchManuals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      // Use assets API but filter to manuals folder
      const params = new URLSearchParams();
      params.append('category', 'manuals');

      const url = `${API_URL}/assets?${params.toString()}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch manuals');
      }

      const data: ManualsResponse = await response.json();
      if (data.success) {
        setManuals(data.data.assets);
      }
    } catch (err) {
      console.error('Failed to fetch manuals:', err);
      setError('Failed to load manuals. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchManuals();
  }, [fetchManuals]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!manualTitle) {
        setManualTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!manualTitle) {
        setManualTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !manualTitle) return;

    setIsUploading(true);
    setUploadError(null);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', manualTitle);
      formData.append('folder', 'manuals');
      formData.append('category', 'manuals');
      formData.append('audience', manualTopic);
      if (manualDescription) {
        formData.append('description', manualDescription);
      }

      const response = await fetch(`${API_URL}/assets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      // Reset form and close modal
      setSelectedFile(null);
      setManualTitle('');
      setManualDescription('');
      setManualTopic('getting-started');
      setShowUploadModal(false);

      // Refresh manuals list
      fetchManuals();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(errorMessage);
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (manual: Manual) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/assets/${manual.id}/stream`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = manual.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Refresh to update download count
      fetchManuals();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (manual: Manual) => {
    if (!confirm(`Are you sure you want to delete "${manual.name}"?`)) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/assets/${manual.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      // Refresh manuals list
      fetchManuals();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type === 'application/pdf') return <FileText className="h-8 w-8 text-red-500" />;
    if (file.type.startsWith('video/')) return <FileVideo className="h-8 w-8 text-purple-500" />;
    return <BookOpen className="h-8 w-8 text-muted-foreground" />;
  };

  const getManualIcon = (fileType: string | null) => {
    if (fileType === 'pdf') return <FileText className="h-8 w-8 text-red-500" />;
    if (fileType === 'video') return <FileVideo className="h-8 w-8 text-purple-500" />;
    return <BookOpen className="h-8 w-8 text-primary" />;
  };

  // Filter manuals by search query and type
  const filteredManuals = manuals.filter((manual) => {
    const matchesSearch =
      !searchQuery || manual.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || manual.file_type === selectedType;
    const matchesCategory = selectedCategory === 'all' || manual.audience === selectedCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">MANUALS & GUIDES</h1>
          <p className="text-muted-foreground">
            Comprehensive training resources, playbooks, and documentation.
          </p>
        </div>
        {canUpload && (
          <Button
            onClick={() => {
              setUploadError(null);
              setShowUploadModal(true);
            }}
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Manual
          </Button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search manuals and guides..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          options={categoryOptions}
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full sm:w-[180px]"
        />
        <Select
          options={typeOptions}
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="w-full sm:w-[160px]"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading manuals...</span>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" className="mt-4" onClick={fetchManuals}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredManuals.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Manuals</CardTitle>
            <CardDescription>Training documentation and guides</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-12">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No Manuals Found</p>
            <p className="text-sm text-muted-foreground mt-2">
              {searchQuery || selectedType !== 'all'
                ? 'Try adjusting your filters.'
                : isAdmin
                  ? 'Click "Upload Manual" to add training resources.'
                  : 'No manuals have been uploaded yet.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Manuals Grid */}
      {!isLoading && !error && filteredManuals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredManuals.map((manual) => (
            <Card key={manual.id} className="group hover:border-primary/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-muted rounded-lg">
                    {getManualIcon(manual.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate" title={manual.name}>
                      {manual.name}
                    </h3>
                    <p className="text-sm text-muted-foreground capitalize">
                      {manual.file_type || 'Document'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {manual.download_count} downloads
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleDownload(manual)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(manual)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Manual</DialogTitle>
            <DialogDescription>
              Upload training documentation and guides. Supported formats: PDF, Video.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload Error */}
            {uploadError && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {uploadError}
              </div>
            )}

            {/* Drop Zone */}
            <div
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              // biome-ignore lint/a11y/useSemanticElements: Drop zone div needs role for accessibility
              role="button"
              tabIndex={0}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-3">
                    {getFileIcon(selectedFile)}
                    <div className="text-left">
                      <p className="font-medium truncate max-w-[200px]">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    Drag & drop a file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, Video (max 100MB)</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ALL_ACCEPTED}
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Manual Details */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                  placeholder="Manual title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder="Brief description of the manual"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="topic">Topic</Label>
                <Select
                  options={categoryOptions.filter((c) => c.value !== 'all')}
                  value={manualTopic}
                  onChange={(e) => setManualTopic(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || !manualTitle || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
